export interface Sprite {
  readonly image: CanvasImageSource;
  readonly sx: number;
  readonly sy: number;
  readonly sw: number;
  readonly sh: number;
}

export interface SpriteCss {
  readonly url: string;
  readonly backgroundSize: string;
  readonly backgroundPosition: string;
  readonly width: number;
  readonly height: number;
}

interface SheetInfo {
  readonly file: string;
  readonly width: number;
  readonly height: number;
}

interface GroupManifest {
  readonly eager: boolean;
  readonly sheets: readonly SheetInfo[];
  readonly sprites: Readonly<Record<string, readonly [number, number, number, number, number]>>;
}

export interface AtlasManifest {
  readonly groups: Readonly<Record<string, GroupManifest>>;
  readonly singles: Readonly<Record<string, string>>;
  readonly sounds: Readonly<
    Record<
      string,
      {
        readonly eager: boolean;
        readonly file: string;
        readonly clips: Readonly<
          Record<string, { readonly offset: number; readonly duration: number }>
        >;
      }
    >
  >;
}

type GroupState = {
  readonly manifest: GroupManifest;
  readonly images: (HTMLImageElement | null)[];
  requested: boolean;
};

export class Atlas {
  private manifest: AtlasManifest | null = null;
  private readonly groups = new Map<string, GroupState>();
  private readonly owner = new Map<string, string>();
  private loading: Promise<void> | null = null;

  constructor(
    private readonly prefix: string,
    private readonly fetchJson: (url: string) => Promise<AtlasManifest> = defaultFetchJson,
  ) {}

  load(): Promise<void> {
    this.loading ??= this.loadOnce();
    return this.loading;
  }

  private readonly listeners = new Set<() => void>();

  get ready(): boolean {
    return this.manifest !== null;
  }

  private async loadOnce(): Promise<void> {
    const manifest = await this.fetchJson(`${this.prefix}/atlas.json`);
    this.manifest = manifest;
    for (const [name, group] of Object.entries(manifest.groups)) {
      this.groups.set(name, {
        manifest: group,
        images: group.sheets.map(() => null),
        requested: false,
      });
      for (const sprite of Object.keys(group.sprites)) this.owner.set(sprite, name);
    }
    for (const [name, group] of this.groups) {
      if (group.manifest.eager) this.request(name);
    }
  }

  single(pngPath: string): string | null {
    const rel = this.manifest?.singles[pngPath];
    return rel === undefined ? null : `${this.prefix}/${rel}`;
  }

  soundSprite(name: string): AtlasManifest['sounds'][string] | null {
    return this.manifest?.sounds[name] ?? null;
  }

  get soundSpriteNames(): readonly string[] {
    return Object.keys(this.manifest?.sounds ?? {});
  }

  sprite(name: string): Sprite | null {
    const groupName = this.owner.get(name);
    if (groupName === undefined) return null;
    const group = this.groups.get(groupName);
    if (group === undefined) return null;

    if (!group.requested) this.request(groupName);

    const entry = group.manifest.sprites[name];
    if (entry === undefined) return null;
    const image = group.images[entry[0]];
    if (image === null || image === undefined) return null;
    return { image, sx: entry[1], sy: entry[2], sw: entry[3], sh: entry[4] };
  }

  spriteCss(name: string, size: number): SpriteCss | null {
    const groupName = this.owner.get(name);
    if (groupName === undefined) return null;
    const group = this.groups.get(groupName);
    const entry = group?.manifest.sprites[name];
    if (group === undefined || entry === undefined) return null;
    if (!group.requested) this.request(groupName);
    const sheet = group.manifest.sheets[entry[0]];
    if (sheet === undefined) return null;

    const scale = size / Math.max(entry[3], entry[4]);
    return {
      url: `${this.prefix}/${sheet.file}`,
      backgroundSize: `${String(sheet.width * scale)}px ${String(sheet.height * scale)}px`,
      backgroundPosition: `${String(-entry[1] * scale)}px ${String(-entry[2] * scale)}px`,
      width: entry[3] * scale,
      height: entry[4] * scale,
    };
  }

  /** Stretched non-uniformly to fill an exact `size`x`size` box: preserving aspect ratio would turn a wide asset like the Rainbow trail's 128x1 frame into an invisible sliver. */
  spriteCssStretched(name: string, size: number): SpriteCss | null {
    const groupName = this.owner.get(name);
    if (groupName === undefined) return null;
    const group = this.groups.get(groupName);
    const entry = group?.manifest.sprites[name];
    if (group === undefined || entry === undefined) return null;
    if (!group.requested) this.request(groupName);
    const sheet = group.manifest.sheets[entry[0]];
    if (sheet === undefined) return null;

    const scaleX = size / entry[3];
    const scaleY = size / entry[4];
    return {
      url: `${this.prefix}/${sheet.file}`,
      backgroundSize: `${String(sheet.width * scaleX)}px ${String(sheet.height * scaleY)}px`,
      backgroundPosition: `${String(-entry[1] * scaleX)}px ${String(-entry[2] * scaleY)}px`,
      width: size,
      height: size,
    };
  }

  /** `ready` is true once the manifest is parsed, before any pixels exist — a caller that samples `sprite()` on that signal alone gets null. */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private request(groupName: string): void {
    const group = this.groups.get(groupName);
    if (group === undefined || group.requested) return;
    group.requested = true;
    for (const [i, sheet] of group.manifest.sheets.entries()) {
      const image = new Image();
      image.onload = () => {
        group.images[i] = image;
        for (const listener of this.listeners) listener();
      };
      image.src = `${this.prefix}/${sheet.file}`;
    }
  }
}

async function defaultFetchJson(url: string): Promise<AtlasManifest> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${url}: ${String(response.status)}`);
  return (await response.json()) as AtlasManifest;
}

export function drawSprite(
  ctx: CanvasRenderingContext2D,
  sprite: Sprite,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
): void {
  ctx.drawImage(sprite.image, sprite.sx, sprite.sy, sprite.sw, sprite.sh, dx, dy, dw, dh);
}
